import { useParams, useRouter } from '@/lib/navigation';
import { useTranslations } from '@/i18n';
import TopPanel from '@/components/ui/TopPanel';
import { Button } from '@/components/ui/button';
import { useTrip } from '@/hooks/trips/useTrip';
import { useDeleteTrip } from '@/hooks/trips/useDeleteTrip';
import { useSendTrip } from '@/hooks/trips/useSendTrip';
import { useApproveTrip } from '@/hooks/trips/useApproveTrip';
import { useDeclineTrip } from '@/hooks/trips/useDeclineTrip';
import { useReturnTrip } from '@/hooks/trips/useReturnTrip';
import { useCostCentre } from '@/hooks/trips/useCostCentre';
import { useUser } from '@/hooks/auth/useUser';
import type {
  BusinessTripStatus,
  TripDetail,
  TripDetailCity,
  TripCityTransportation,
} from '@/hooks/trips/types';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { format, parseISO, isBefore, isAfter, startOfDay } from 'date-fns';
import { useMemo, useState, useEffect } from 'react';
import { MoreHorizontalIcon } from 'lucide-react';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import InputDialog from '@/components/ui/InputDialog';
import AddCityCard from '@/components/trips/AddCityCard';
import CitiesList from '@/components/trips/CitiesList';
import TripSummaryCard from '@/components/trips/TripSummaryCard';
import TripActivityCard from '@/components/trips/TripActivityCard';
import MissingExpensesDialog, {
  type MissingExpenseEntry,
  type MissingExpenseType,
} from '@/components/trips/MissingExpensesDialog';
import InvalidCityDatesDialog from '@/components/trips/InvalidCityDatesDialog';
import IncompleteDateCoverageDialog from '@/components/trips/IncompleteDateCoverageDialog';
import InvalidHotelDatesDialog, {
  type HotelDateValidationEntry,
} from '@/components/trips/InvalidHotelDatesDialog';
import {
  validateCityDates,
  getCitiesWithDateIssues,
  findUncoveredDateRanges,
  type CityDateValidationResult,
  type CityForValidation,
  type DateGap,
} from '@/lib/city-date-validation';
import { getStatusBadge } from '@/components/trips/table/trip-list-utils';
import TripApprovalStepper from '@/components/trips/TripApprovalStepper';
import TripApprovalActions from '@/components/trips/TripApprovalActions';
import TripBookingChecklist from '@/components/trips/TripBookingChecklist';
import TripRoamingActivationCard from '@/components/trips/TripRoamingActivationCard';
import TripFuelManagementCard from '@/components/trips/TripFuelManagementCard';
import { TripCostsComparisonCard, type VisibleCostCategory } from '@/components/trips/costs';
import { useSendTripToAccountant } from '@/hooks/trips/useSendTripToAccountant';
import { useToast } from '@/components/ui/toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const APPROVAL_PHASE_STATUSES: BusinessTripStatus[] = ['pending', 'finalizing', 'declined'];

// Georgian prefix "მჭირდება" (I need) - used to strip from labels for mobile view
const NEEDS_PREFIX_KA = 'მჭირდება';
// English prefix "I need" - used to strip from labels for mobile view
const NEEDS_PREFIX_EN = 'i need';

function trimNeedsPrefix(label: string): string {
  const trimmed = typeof label === 'string' ? label.trim() : '';
  if (!trimmed) {
    return '';
  }

  const normalized = trimmed.toLowerCase();

  // Check for Georgian prefix
  if (normalized.startsWith(NEEDS_PREFIX_KA.toLowerCase())) {
    return trimmed.slice(NEEDS_PREFIX_KA.length).trim();
  }

  // Check for English prefix
  if (normalized.startsWith(NEEDS_PREFIX_EN)) {
    return trimmed.slice(NEEDS_PREFIX_EN.length).trim();
  }

  return trimmed;
}

function mapCostTypeToCategory(value: string): VisibleCostCategory | null {
  switch (value) {
    case 'hotel':
      return 'hotel';
    case 'transportation':
      return 'transportation';
    case 'fuel':
    case 'daily':
    case 'other':
      return 'other';
    default:
      return null;
  }
}

/**
 * Transportation types that don't require cost uploads (company handles these)
 */
const TRANSPORTATION_TYPES_WITHOUT_UPLOAD: string[] = ['rental_car', 'car_with_driver'];

/**
 * Checks if a city requires transportation cost upload.
 * Returns false if all transportations are rental_car or car_with_driver.
 */
function cityRequiresTransportationUpload(city: TripDetailCity): boolean {
  if (!city.needTransportation) {
    return false;
  }

  // If no transportations added yet, we can't determine the type - assume required
  if (!city.transportations || city.transportations.length === 0) {
    return true;
  }

  // Check if ANY transportation requires upload (is ticket_or_advance)
  return city.transportations.some(
    (t: TripCityTransportation) =>
      !t.assistanceType || !TRANSPORTATION_TYPES_WITHOUT_UPLOAD.includes(t.assistanceType)
  );
}

function computeMissingCostUploads(trip: TripDetail | undefined): VisibleCostCategory[] {
  if (!trip) {
    return [];
  }

  const needsHotel = trip.cities?.some((city) => city.needHotel);
  // Only require transportation upload if at least one city has ticket_or_advance
  const needsTransportationUpload = trip.cities?.some(cityRequiresTransportationUpload);
  const costs = trip.costs ?? [];

  const hasHotelCost = costs.some((cost) => mapCostTypeToCategory(cost.costType) === 'hotel');
  const hasTransportationCost = costs.some(
    (cost) => mapCostTypeToCategory(cost.costType) === 'transportation'
  );

  const missing: VisibleCostCategory[] = [];

  if (needsHotel && !hasHotelCost) {
    missing.push('hotel');
  }

  if (needsTransportationUpload && !hasTransportationCost) {
    missing.push('transportation');
  }

  return missing;
}

export function TripDetailPage() {
  const params = useParams();
  const router = useRouter();
  const tripId = params.id as string;

  const t = useTranslations('tripForm');
  const tFields = useTranslations('tripForm.fields');
  const tExpenses = useTranslations('tripForm.expenses');
  const tNav = useTranslations('navigation');
  const tTrips = useTranslations('trips');
  const tActions = useTranslations('trips.actions');
  const tToasts = useTranslations('toasts');
  const { showToast } = useToast();

  const roamingExpenseLabel = tExpenses('roaming');
  const accommodationExpenseLabel = tExpenses('accommodation');
  const mobileRoamingTitle = useMemo(
    () => trimNeedsPrefix(roamingExpenseLabel),
    [roamingExpenseLabel]
  );
  const mobileDailyTitle = useMemo(
    () => trimNeedsPrefix(accommodationExpenseLabel),
    [accommodationExpenseLabel]
  );

  const { data: trip, isLoading, isError } = useTrip(tripId);
  const { data: currentUser } = useUser();

  const initiatorId = useMemo(() => {
    if (!trip?.initiator) return undefined;
    if (typeof trip.initiator === 'string') return Number(trip.initiator);
    return Number(trip.initiator.id);
  }, [trip?.initiator]);

  const { data: costCentreData } = useCostCentre({
    initiatorId,
    enabled: Boolean(initiatorId),
  });

  const costCentreValue = useMemo(() => {
    const rawValue = costCentreData?.cost_centre;
    if (!rawValue || typeof rawValue !== 'string') return '';
    const trimmed = rawValue.trim();
    return trimmed.toLowerCase() === 'false' ? '' : trimmed;
  }, [costCentreData]);

  const deleteTripMutation = useDeleteTrip();
  const sendTripMutation = useSendTrip();
  const approveTripMutation = useApproveTrip();
  const declineTripMutation = useDeclineTrip();
  const returnTripMutation = useReturnTrip();
  const sendToAccountantMutation = useSendTripToAccountant();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showMissingExpenses, setShowMissingExpenses] = useState(false);
  const [missingExpenses, setMissingExpenses] = useState<MissingExpenseEntry[]>([]);
  const [showInvalidCityDates, setShowInvalidCityDates] = useState(false);
  const [invalidCityDatesEntries, setInvalidCityDatesEntries] = useState<
    CityDateValidationResult[]
  >([]);
  const [showIncompleteCoverage, setShowIncompleteCoverage] = useState(false);
  const [uncoveredDateGaps, setUncoveredDateGaps] = useState<DateGap[]>([]);
  const [showInvalidHotelDates, setShowInvalidHotelDates] = useState(false);
  const [invalidHotelDatesEntries, setInvalidHotelDatesEntries] = useState<
    HotelDateValidationEntry[]
  >([]);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showSendToAccountantConfirm, setShowSendToAccountantConfirm] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [missingCostCategories, setMissingCostCategories] = useState<VisibleCostCategory[]>([]);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('ka-GE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    []
  );

  const formatCurrency = (amount?: string | number) => {
    if (amount == null || amount === '') {
      return '—';
    }

    const numeric = typeof amount === 'string' ? Number(amount) : amount;
    if (!Number.isFinite(numeric)) {
      return typeof amount === 'string' ? amount : String(amount);
    }

    return `₾ ${currencyFormatter.format(numeric)}`;
  };

  const handleDeleteTrip = () => {
    deleteTripMutation.mutate(tripId);
    setShowDeleteConfirm(false);
  };

  useEffect(() => {
    if (missingCostCategories.length === 0) {
      return;
    }

    const remaining = computeMissingCostUploads(trip);
    if (remaining.length === 0) {
      setMissingCostCategories([]);
    }
  }, [missingCostCategories.length, trip]);

  useEffect(() => {
    if (!trip || missingCostCategories.length === 0) {
      return;
    }

    const currentUserId = currentUser?.id ?? null;
    const isAuthorMatch = currentUserId != null && trip.author?.id === currentUserId;
    const isInitiatorMatch =
      currentUserId != null &&
      ((typeof trip.initiator === 'object' && trip.initiator?.id === currentUserId) ||
        (typeof trip.initiator === 'string' && trip.initiator === String(currentUserId)));

    const cardVisible = trip.status === 'approved' && (isAuthorMatch || isInitiatorMatch);

    if (!cardVisible) {
      setMissingCostCategories([]);
    }
  }, [currentUser, missingCostCategories.length, trip]);

  // Compute city date validation issues for highlighting (must be before early returns)
  const invalidCityIds = useMemo(() => {
    if (!trip?.cities || trip.cities.length === 0 || !trip?.startDate || !trip?.endDate) {
      return new Set<number>();
    }

    const currentUserId = currentUser?.id ?? null;
    const isAuthor = currentUserId != null && trip.author?.id === currentUserId;
    const canModify = (trip.status === 'draft' || trip.status === 'returned_back') && isAuthor;

    if (!canModify) {
      // Only show validation highlighting when user can edit
      return new Set<number>();
    }

    const citiesForValidation: CityForValidation[] = trip.cities.map((city) => ({
      id: city.id,
      cityName: city.cityName,
      arrivalDate: city.arrivalDate,
      departureDate: city.departureDate,
    }));

    return getCitiesWithDateIssues(citiesForValidation, trip.startDate, trip.endDate);
  }, [
    trip?.cities,
    trip?.startDate,
    trip?.endDate,
    trip?.status,
    trip?.author?.id,
    currentUser?.id,
  ]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F5F5F7]">
        <Spinner variant="circle" />
      </div>
    );
  }

  if (isError || !trip) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F5F5F7]">
        <div className="text-center">
          <p className="mb-4 text-lg text-[#666666]">{tTrips('notFound')}</p>
          <Button onClick={() => router.push('/')}>{tNav('back')}</Button>
        </div>
      </div>
    );
  }

  const currentUserId = currentUser?.id ?? null;
  const isAuthor = currentUserId != null && trip.author?.id === currentUserId;
  const isInitiator =
    currentUserId != null &&
    ((typeof trip.initiator === 'object' && trip.initiator?.id === currentUserId) ||
      (typeof trip.initiator === 'string' && trip.initiator === String(currentUserId)));
  const isApprovalPhase = APPROVAL_PHASE_STATUSES.includes(trip.status);
  const tripCitiesCount = trip.cities?.length ?? 0;
  const hasCities = tripCitiesCount > 0;

  const showRoamingCard = trip.tripType === 'international' && Boolean(trip.roamingExpense);
  const showDailyCard = Boolean(trip.accommodationExpense);
  const dailyLimitLabel = t('fixedDailyLimit', {
    amount: formatCurrency(trip.accommodationAmount),
  });
  const shouldShowApprovalStepper = isApprovalPhase && (trip.approvalSteps?.length ?? 0) > 0;

  // Use availableActions from API instead of role-based checks
  const availableActions = trip.availableActions ?? [];
  const canApprove = availableActions.includes('approve');
  const canDecline = availableActions.includes('decline');
  const canReturnBack = availableActions.includes('return_back');
  const canSubmit = availableActions.includes('submit') || availableActions.includes('resubmit');
  const canSendToAccountant = availableActions.includes('accounting_review');
  const canDelete = trip.status === 'draft' && (isAuthor || isInitiator); // Delete allowed for author or initiator
  // Allow editing if user can submit/resubmit the trip
  const canModifyCities = canSubmit;
  const canActivateRoaming = availableActions.includes('activate_roaming');
  const canBook = availableActions.includes('book');
  const canUpdateFuel = availableActions.includes('update_fuel');

  const showSendButton = canSubmit && hasCities;
  const showSavePlaceholder = canSubmit && !hasCities;

  // Show costs card for:
  // - 'approved' status (always)
  // - 'returned_back' status only if approvedAt is set (means it was approved before being returned)
  // - 'finalizing' or 'finished' status (always - costs should exist at this stage)
  const shouldShowCostComparisonCard =
    (isAuthor || isInitiator) &&
    (trip.status === 'approved' ||
      (trip.status === 'returned_back' && Boolean(trip.approvedAt)) ||
      trip.status === 'finalizing' ||
      trip.status === 'finished');

  // Costs are only editable in 'approved' or 'returned_back' (with approvedAt) statuses
  // In 'finalizing' and 'finished' statuses, costs are read-only
  const canManageCosts =
    (isAuthor || isInitiator) &&
    (trip.status === 'approved' || (trip.status === 'returned_back' && Boolean(trip.approvedAt)));

  const canDeleteTrip = canDelete;

  const citiesWithBookingSupport = (trip.cities ?? []).filter((city) => {
    const requiresHotelHelp = city.hotels?.some((hotel) => hotel.bookingSupport) ?? false;
    const requiresTransportHelp =
      city.transportations?.some((transportation) => transportation.bookingSupport) ?? false;
    return requiresHotelHelp || requiresTransportHelp;
  });
  const citiesWithFuel = (trip.cities ?? []).filter((city) => (city.fuels?.length ?? 0) > 0);
  const shouldShowBookingChecklist = canBook && citiesWithBookingSupport.length > 0;
  const hasPendingBookingTasks =
    canBook &&
    citiesWithBookingSupport.some((city) => {
      const pendingHotels =
        city.hotels?.some((hotel) => hotel.bookingSupport && !hotel.isBooked) ?? false;
      const pendingTransportations =
        city.transportations?.some(
          (transportation) => transportation.bookingSupport && !transportation.isBooked
        ) ?? false;
      return pendingHotels || pendingTransportations;
    });
  const shouldDisableApproveAction = approveTripMutation.isPending || hasPendingBookingTasks;
  const shouldShowRoamingActivationCard = canActivateRoaming && Boolean(trip.roamingExpense);
  const shouldShowFuelManagementCard = canUpdateFuel && citiesWithFuel.length > 0;
  const pendingApproverId =
    trip.pendingApproverId != null ? String(trip.pendingApproverId) : undefined;
  const isCurrentApprover =
    pendingApproverId != null && currentUserId != null && pendingApproverId === currentUserId;
  const showApprovalActions = canApprove || canDecline || canReturnBack;
  function collectMissingExpenses(): MissingExpenseEntry[] {
    if (!trip?.cities || trip.cities.length === 0) {
      return [];
    }

    return trip.cities.reduce<MissingExpenseEntry[]>((reports, city) => {
      const missing: MissingExpenseType[] = [];

      if (city.needHotel && (!city.hotels || city.hotels.length === 0)) {
        missing.push('accommodation');
      }

      if (city.needTransportation && (!city.transportations || city.transportations.length === 0)) {
        missing.push('transport');
      }

      if (city.needFuel && (!city.fuels || city.fuels.length === 0)) {
        missing.push('fuel');
      }

      if (missing.length > 0) {
        reports.push({
          cityId: city.id,
          cityName: city.cityName,
          missing,
        });
      }

      return reports;
    }, []);
  }

  const ensureCostUploadsPresent = () => {
    if (!shouldShowCostComparisonCard) {
      setMissingCostCategories([]);
      return true;
    }

    const missingUploads = computeMissingCostUploads(trip);
    if (missingUploads.length > 0) {
      setMissingCostCategories(missingUploads);
      return false;
    }

    setMissingCostCategories([]);
    return true;
  };

  const handleSendTrip = () => {
    if (!ensureCostUploadsPresent()) {
      setShowSendConfirm(false);
      return;
    }

    sendTripMutation.mutate(
      { tripId, status: trip?.status ?? 'draft' },
      {
        onSuccess: () => {
          showToast({ message: tToasts('tripSubmitted'), variant: 'success' });
        },
      }
    );
    setShowSendConfirm(false);
  };

  const handleSendToAccountant = () => {
    if (!ensureCostUploadsPresent()) {
      setShowSendToAccountantConfirm(false);
      return;
    }

    sendToAccountantMutation.mutate(
      { tripId },
      {
        onSuccess: () => {
          setShowSendToAccountantConfirm(false);
          showToast({ message: tToasts('costsSubmitted'), variant: 'success' });
        },
        onError: () => setShowSendToAccountantConfirm(false),
      }
    );
  };

  function collectInvalidCityDates(): CityDateValidationResult[] {
    if (!trip?.cities || trip.cities.length === 0 || !trip.startDate || !trip.endDate) {
      return [];
    }

    const citiesForValidation: CityForValidation[] = trip.cities.map((city) => ({
      id: city.id,
      cityName: city.cityName,
      arrivalDate: city.arrivalDate,
      departureDate: city.departureDate,
    }));

    return validateCityDates(citiesForValidation, trip.startDate, trip.endDate);
  }

  function collectUncoveredDateGaps(): DateGap[] {
    if (!trip?.cities || trip.cities.length === 0 || !trip.startDate || !trip.endDate) {
      return [];
    }

    const citiesForValidation: CityForValidation[] = trip.cities.map((city) => ({
      id: city.id,
      cityName: city.cityName,
      arrivalDate: city.arrivalDate,
      departureDate: city.departureDate,
    }));

    return findUncoveredDateRanges(citiesForValidation, trip.startDate, trip.endDate);
  }

  function collectInvalidHotelDates(): HotelDateValidationEntry[] {
    if (!trip?.cities || trip.cities.length === 0) {
      return [];
    }

    const issues: HotelDateValidationEntry[] = [];

    for (const city of trip.cities) {
      if (!city.hotels || city.hotels.length === 0) {
        continue;
      }

      const cityArrival = startOfDay(parseISO(city.arrivalDate));
      const cityDeparture = startOfDay(parseISO(city.departureDate));

      for (const hotel of city.hotels) {
        const hotelCheckIn = startOfDay(parseISO(hotel.checkInDate));
        const hotelCheckOut = startOfDay(parseISO(hotel.checkOutDate));

        const checkInBeforeArrival = isBefore(hotelCheckIn, cityArrival);
        const checkOutAfterDeparture = isAfter(hotelCheckOut, cityDeparture);

        if (checkInBeforeArrival || checkOutAfterDeparture) {
          let issue: HotelDateValidationEntry['issue'];
          if (checkInBeforeArrival && checkOutAfterDeparture) {
            issue = 'both';
          } else if (checkInBeforeArrival) {
            issue = 'check_in_before_arrival';
          } else {
            issue = 'check_out_after_departure';
          }

          issues.push({
            cityId: city.id,
            cityName: city.cityName,
            hotelId: hotel.id,
            checkInDate: hotel.checkInDate,
            checkOutDate: hotel.checkOutDate,
            issue,
          });
        }
      }
    }

    return issues;
  }

  const handleSendClick = () => {
    if (!hasCities) {
      setShowSendConfirm(true);
      return;
    }

    // STEP 0: Check for city date validation issues (outside trip range, overlaps)
    const dateIssues = collectInvalidCityDates();
    if (dateIssues.length > 0) {
      setInvalidCityDatesEntries(dateIssues);
      setShowInvalidCityDates(true);
      return;
    }

    // STEP 1: Check for invalid hotel dates (outside city date range)
    const hotelDateIssues = collectInvalidHotelDates();
    if (hotelDateIssues.length > 0) {
      setInvalidHotelDatesEntries(hotelDateIssues);
      setShowInvalidHotelDates(true);
      return;
    }

    // STEP 2: Check for incomplete date coverage (gaps in trip period)
    const gaps = collectUncoveredDateGaps();
    if (gaps.length > 0) {
      setUncoveredDateGaps(gaps);
      setShowIncompleteCoverage(true);
      return;
    }

    const reports = collectMissingExpenses();
    if (reports.length > 0) {
      setMissingExpenses(reports);
      setShowMissingExpenses(true);
      return;
    }

    if (!ensureCostUploadsPresent()) {
      return;
    }

    setShowSendConfirm(true);
  };

  const handleSendToAccountantClick = () => {
    if (!ensureCostUploadsPresent()) {
      return;
    }

    setShowSendToAccountantConfirm(true);
  };

  const closeMissingExpensesDialog = () => {
    setShowMissingExpenses(false);
  };

  const handleApproveTripAction = () => {
    setShowApproveConfirm(true);
  };

  const handleRejectTripAction = () => {
    setShowRejectDialog(true);
  };

  const handleEditTripAction = () => {
    setShowReturnDialog(true);
  };

  const overflowActions: Array<{
    id: string;
    label: string;
    onSelect?: () => void;
    disabled?: boolean;
    loading?: boolean;
  }> = [];

  let primaryAction: {
    id: string;
    label: string;
    onPress?: () => void;
    disabled?: boolean;
    loading?: boolean;
    buttonVariant: 'brand' | 'ghost';
    icon?: string;
  } | null = null;

  if (showSendButton) {
    primaryAction = {
      id: 'send',
      label: t('send'),
      onPress: handleSendClick,
      disabled: sendTripMutation.isPending,
      loading: sendTripMutation.isPending,
      buttonVariant: 'brand',
      icon: '/assets/icons/file-check.svg',
    };

    if (canSendToAccountant) {
      overflowActions.push({
        id: 'sendToAccountant',
        label: tTrips('actions.sendToAccountant'),
        onSelect: handleSendToAccountantClick,
        disabled: sendToAccountantMutation.isPending,
        loading: sendToAccountantMutation.isPending,
      });
    }
  } else if (canSendToAccountant) {
    primaryAction = {
      id: 'sendToAccountant',
      label: t('send'),
      onPress: handleSendToAccountantClick,
      disabled: sendToAccountantMutation.isPending,
      loading: sendToAccountantMutation.isPending,
      buttonVariant: 'brand',
      icon: '/assets/icons/file-check.svg',
    };
  }

  if (showSavePlaceholder) {
    const saveAction = {
      id: 'savePlaceholder',
      label: t('saveAndContinue'),
      onSelect: undefined,
      disabled: true,
      loading: false,
    };

    if (primaryAction) {
      overflowActions.push(saveAction);
    } else {
      primaryAction = {
        id: 'savePlaceholder',
        label: t('saveAndContinue'),
        buttonVariant: 'ghost',
        disabled: true,
      };
    }
  }

  const mobileOverflowAriaLabel =
    overflowActions.length === 1
      ? overflowActions[0]?.label
      : overflowActions.length > 1
        ? overflowActions.map((action) => action.label).join(', ')
        : undefined;

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Fixed TopPanel */}
      <div className="fixed top-0 right-0 left-0 z-50">
        <TopPanel />
      </div>

      <main className="bg-white pt-[3.5rem] pb-[calc(7rem+env(safe-area-inset-bottom))] md:pt-[5.875rem]">
        {shouldShowApprovalStepper && (
          <TripApprovalStepper
            steps={trip.approvalSteps}
            pendingApproverId={trip.pendingApproverId ?? null}
            fullWidth
            className="mb-8"
          />
        )}

        <div className="relative z-20 mx-auto max-w-[76.9375rem] px-4 pb-[2.62rem] sm:px-6 lg:px-8">
          {/* Page Title Section with Delete Button */}
          <div className="flex flex-col gap-4 pb-[2.5rem] lg:mt-[2.62rem] lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-full">
              <h1 className="mb-2 text-[2rem] leading-[2.5rem] font-medium text-[#292929]">
                {hasCities ? t('pageTitleWithCities') : t('pageTitle')}
              </h1>
              <p className="flex flex-wrap items-center gap-2 text-base leading-6 text-[#666666]">
                <span>
                  {t('tripNumber')} -{' '}
                  <span className="font-medium text-[#292929]">{trip.number}</span>
                </span>
                {trip.parentId &&
                  trip.parentState &&
                  getStatusBadge(trip.parentState, tTrips, trip.parentStateLabel)}
              </p>
            </div>

            {/* Delete Button */}
            {canDeleteTrip && (
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                variant="unstyled"
                size="none"
                className="flex w-fit items-center gap-2 self-start rounded-[2rem] bg-[#ff66660a] px-4 py-2.5 pl-2.5 transition-colors hover:bg-[#ff666614] disabled:cursor-not-allowed disabled:opacity-60"
                disabled={deleteTripMutation.isPending}
              >
                <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                  <img src="/assets/icons/delete.svg" alt="" className="h-5 w-5" />
                </div>
                <span className="text-sm leading-5 text-[#e5484d]">{tActions('deleteTrip')}</span>
              </Button>
            )}
          </div>

          {/* Summary Card - Only show when cities exist */}
          {hasCities && (
            <>
              <div className="pb-6 lg:hidden">
                <TripSummaryCard
                  tripStartDate={trip.startDate}
                  tripEndDate={trip.endDate}
                  expenseTotals={trip.expenseTotals}
                  citiesCount={tripCitiesCount}
                  status={trip.status}
                  collapsible
                  defaultOpen={false}
                />
              </div>
              <div className="hidden pb-8 lg:block">
                <TripSummaryCard
                  tripStartDate={trip.startDate}
                  tripEndDate={trip.endDate}
                  expenseTotals={trip.expenseTotals}
                  citiesCount={tripCitiesCount}
                  status={trip.status}
                />
              </div>
            </>
          )}

          {shouldShowCostComparisonCard && (
            <div className="pb-8">
              <TripCostsComparisonCard
                tripId={tripId}
                trip={trip}
                canManage={canManageCosts}
                highlightCategories={missingCostCategories}
              />
            </div>
          )}

          {shouldShowBookingChecklist && (
            <TripBookingChecklist
              tripId={tripId}
              cities={citiesWithBookingSupport}
              formatCurrency={formatCurrency}
            />
          )}

          {shouldShowRoamingActivationCard && (
            <div className="pb-8">
              <TripRoamingActivationCard
                tripId={tripId}
                phoneNumber={trip.phoneNumber}
                isActivated={trip.roamingActivated ?? null}
              />
            </div>
          )}

          {shouldShowFuelManagementCard && (
            <div className="pb-8">
              <TripFuelManagementCard
                tripId={tripId}
                cities={citiesWithFuel}
                canEdit={isCurrentApprover}
              />
            </div>
          )}

          {/* Activity Card - Only show when cities exist */}
          {hasCities && trip.approvalHistory && trip.approvalHistory.length > 0 && (
            <div className="pb-8">
              <TripActivityCard approvalHistory={trip.approvalHistory} defaultOpen={false} />
            </div>
          )}

          {/* Main Content Area - Two Column Layout */}
          <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
            {/* Left Column - General Information Card */}
            <div className="w-full lg:max-w-[36.375rem] lg:flex-shrink-0">
              <div className="rounded-3xl border border-[#f1f1f1] bg-white p-5 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]">
                {/* Header with title and edit icon */}
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-[18px] leading-7 font-bold text-black">{t('generalInfo')}</h2>
                  {canModifyCities && (
                    <Button
                      onClick={() => router.push(`/trips/${tripId}/edit`)}
                      variant="unstyled"
                      size="none"
                      className="shrink-0 rounded-full border border-[#f1f1f1] p-2 transition-colors hover:bg-[#f9f9f9]"
                    >
                      <img src="/assets/icons/edit.svg" alt="Edit" className="h-5 w-5" />
                    </Button>
                  )}
                </div>

                {/* Author section with gray background */}
                <div className="mb-2 flex items-center gap-3 rounded-2xl bg-[#f9f9f9] px-3.5 py-2.5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white">
                    <span className="text-sm font-medium text-[#292929]">
                      {trip.author?.name
                        ? trip.author.name
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .toUpperCase()
                            .slice(0, 2)
                        : '??'}
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-col">
                    <span className="text-[15px] leading-6 text-[#666666]">{t('author')}</span>
                    <span className="text-[14px] leading-5 text-[#292929]">
                      {trip.author?.name || 'Unknown'}
                    </span>
                  </div>
                </div>

                {trip.parentId && trip.parentId !== '0' ? (
                  <>
                    <div className="py-1" />
                    <div className="rounded-2xl bg-[#f9f9f9] px-3.5 py-2.5">
                      <div className="text-[14px] leading-5 text-[#666666]">{t('parentTrip')}</div>
                      <button
                        type="button"
                        onClick={() => router.push(`/trips/${trip.parentId}`)}
                        className="mt-1 inline-flex items-center gap-2 text-[14px] font-medium text-[#292929] underline-offset-4 hover:underline"
                      >
                        <span>{trip.parentRequestNumber ?? trip.parentId}</span>
                        <img
                          src="/assets/icons/chevron-left.svg"
                          alt=""
                          className="h-4 w-4 rotate-180 transform"
                        />
                      </button>
                    </div>
                  </>
                ) : null}

                {/* Form fields */}
                <div className="rounded-2xl bg-white">
                  {/* Trip Type */}
                  <div className="py-3">
                    <div className="mb-1 text-[14px] leading-5 text-[#666666]">
                      {tFields('tripType')}
                    </div>
                    <div className="text-base leading-6 text-[#292929]">
                      {trip.tripType === 'local' ? tFields('local') : tFields('international')}
                    </div>
                  </div>
                  <div className="h-px bg-[#f0f0f0]" />

                  {/* Initiator */}
                  <div className="py-3">
                    <div className="mb-1 text-[14px] leading-5 text-[#666666]">
                      {tFields('initiator')}
                    </div>
                    <div className="text-base leading-6 text-[#292929]">
                      {typeof trip.initiator === 'string'
                        ? trip.initiator
                        : trip.initiator?.name || 'Unknown'}
                    </div>
                  </div>
                  <div className="h-px bg-[#f0f0f0]" />

                  {/* Cost Centre */}
                  <div className="py-3">
                    <div className="mb-1 text-[14px] leading-5 text-[#666666]">
                      {tFields('costCentre')}
                    </div>
                    <div className="text-base leading-6 text-[#999999]">
                      {costCentreValue || '—'}
                    </div>
                  </div>
                  <div className="h-px bg-[#f0f0f0]" />

                  {/* Acting Employee */}
                  <div className="py-3">
                    <div className="mb-1 text-[14px] leading-5 text-[#666666]">
                      {tFields('actingEmployee')}
                    </div>
                    <div className="text-base leading-6 text-[#292929]">
                      {trip.actingEmployee?.name || '—'}
                    </div>
                  </div>
                  <div className="h-px bg-[#f0f0f0]" />

                  {/* Permanent residence country */}
                  <div className="py-3">
                    <div className="mb-1 text-[14px] leading-5 text-[#666666]">
                      {tFields('permanentResidenceCountry')}
                    </div>
                    <div className="text-base leading-6 text-[#292929]">
                      {trip.permanentResidenceCountryName || '—'}
                    </div>
                  </div>
                  <div className="h-px bg-[#f0f0f0]" />

                  {/* Permanent residence city */}
                  <div className="py-3">
                    <div className="mb-1 text-[14px] leading-5 text-[#666666]">
                      {tFields('permanentResidenceCity')}
                    </div>
                    <div className="text-base leading-6 text-[#292929]">
                      {trip.permanentResidenceCityName || '—'}
                    </div>
                  </div>
                  <div className="h-px bg-[#f0f0f0]" />

                  {/* Dates side by side */}
                  <div className="grid grid-cols-2 gap-4 py-3">
                    <div>
                      <div className="mb-1 text-[14px] leading-5 text-[#666666]">
                        {tFields('startDate')}
                      </div>
                      <div className="text-base leading-6 text-[#292929]">
                        {format(parseISO(trip.startDate), 'dd/MM/yyyy')}
                      </div>
                    </div>
                    <div>
                      <div className="mb-1 text-[14px] leading-5 text-[#666666]">
                        {tFields('endDate')}
                      </div>
                      <div className="text-base leading-6 text-[#292929]">
                        {format(parseISO(trip.endDate), 'dd/MM/yyyy')}
                      </div>
                    </div>
                  </div>
                  <div className="h-px bg-[#f0f0f0]" />

                  {/* Purpose */}
                  <div className="py-3">
                    <div className="mb-1 text-[14px] leading-5 text-[#666666]">
                      {tFields('purpose')}
                    </div>
                    <div className="text-base leading-6 text-[#292929]">{trip.purpose}</div>
                  </div>

                  {/* Additional Comment if exists */}
                  {trip.additionalComment && (
                    <>
                      <div className="h-px bg-[#f0f0f0]" />
                      <div className="py-3">
                        <div className="mb-1 text-[14px] leading-5 text-[#666666]">
                          {tFields('additionalCommentDisplay')}
                        </div>
                        <div className="text-base leading-6 text-[#292929]">
                          {trip.additionalComment}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Trip Expenses */}
                {(showRoamingCard || showDailyCard) && (
                  <>
                    <div className="mt-4 hidden space-y-4 lg:block">
                      {/* Roaming expense card */}
                      {showRoamingCard && (
                        <div className="rounded-2xl border border-[#ebebeb] bg-[#f9f9f9] p-5">
                          <div className="flex flex-col gap-5">
                            <div className="flex items-center gap-5">
                              <div className="flex shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-white p-2">
                                <img
                                  src="/assets/icons/phone-wave.svg"
                                  alt=""
                                  className="h-6 w-6"
                                />
                              </div>
                            </div>
                            <div className="flex items-end justify-between">
                              <div className="flex-1">
                                <h3 className="mb-2 text-2xl leading-8 font-medium text-[#292929]">
                                  {tExpenses('roaming')}
                                </h3>
                                <p className="text-[15px] leading-6 text-[#666666]">
                                  {trip.phoneNumber}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="mb-1 text-[15px] leading-6 text-[#666666]">
                                  {t('total')}
                                </p>
                                <p className="text-2xl font-medium text-[#292929]">
                                  {formatCurrency(trip.roamingAmount)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Accommodation expense card */}
                      {showDailyCard && (
                        <div className="rounded-2xl border border-[#ebebeb] bg-[#f9f9f9] p-5">
                          <div className="flex flex-col gap-5">
                            <div className="flex items-center gap-5">
                              <div className="flex shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-white p-2">
                                <img src="/assets/icons/money.svg" alt="" className="h-6 w-6" />
                              </div>
                            </div>
                            <div className="flex items-end justify-between">
                              <div className="flex-1">
                                <h3 className="mb-2 text-2xl leading-8 font-medium text-[#292929]">
                                  {tExpenses('accommodation')}
                                </h3>
                                <p className="text-[15px] leading-6 text-[#666666]">
                                  <span className="whitespace-nowrap">{dailyLimitLabel}</span>
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="mb-1 text-[15px] leading-6 text-[#666666]">
                                  {t('totalFixed')}
                                </p>
                                <p className="text-2xl font-medium text-[#292929]">
                                  {formatCurrency(trip.expenseTotals?.daily ?? 0)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 flex flex-col gap-3 lg:hidden">
                      {showRoamingCard && (
                        <div className="w-full rounded-2xl border border-[#ebebeb] bg-[#f9f9f9] px-4 py-4">
                          <div className="flex min-w-0 items-start gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-white p-2">
                              <img src="/assets/icons/phone-wave.svg" alt="" className="h-6 w-6" />
                            </div>
                            <div className="flex min-w-0 flex-1 flex-col gap-3">
                              <div className="flex min-w-0 items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <h3 className="text-base leading-6 font-medium text-[#292929]">
                                    {mobileRoamingTitle || roamingExpenseLabel}
                                  </h3>
                                  <p className="mt-1 text-sm leading-5 break-words text-[#666666]">
                                    {trip.phoneNumber}
                                  </p>
                                </div>
                                <div className="shrink-0 text-right">
                                  <p className="text-xs leading-4 text-[#666666]">{t('total')}</p>
                                  <p className="text-lg leading-6 font-medium whitespace-nowrap text-[#292929]">
                                    {formatCurrency(trip.roamingAmount)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {showDailyCard && (
                        <div className="w-full rounded-2xl border border-[#ebebeb] bg-[#f9f9f9] px-4 py-5">
                          <div className="flex min-w-0 items-start gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-white p-2">
                              <img src="/assets/icons/money.svg" alt="" className="h-6 w-6" />
                            </div>
                            <div className="flex min-w-0 flex-1 flex-col gap-3">
                              <div className="flex flex-col gap-1">
                                <h3 className="text-base leading-6 font-medium text-[#292929]">
                                  {mobileDailyTitle || accommodationExpenseLabel}
                                </h3>
                                <p className="text-sm leading-5 break-words text-[#666666]">
                                  {dailyLimitLabel}
                                </p>
                              </div>
                              <div className="flex flex-wrap items-end justify-between gap-3">
                                <p className="text-xs leading-4 text-[#666666]">
                                  {t('totalFixed')}
                                </p>
                                <p className="text-lg leading-6 font-medium whitespace-nowrap text-[#292929]">
                                  {formatCurrency(trip.expenseTotals?.daily ?? 0)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Right Column - Cities Section */}
            <div className="w-full lg:flex-1">
              <div className="flex flex-col gap-4">
                {/* Cities List */}
                <CitiesList
                  tripId={tripId}
                  cities={trip.cities}
                  canManageCities={canModifyCities}
                  invalidCityIds={invalidCityIds}
                />

                {/* Add City Card */}
                {canModifyCities && (
                  <AddCityCard
                    onClick={() => {
                      router.push(`/trips/${tripId}/cities/new`);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Corner Logo - kept behind page content */}
        <div className="pointer-events-none fixed right-0 bottom-0 z-10 hidden lg:block">
          <img src="/assets/corner-logo.svg" alt="" className="h-[18.75rem] w-[18.75rem]" />
        </div>

        {/* Fixed Bottom Bar */}
        <div className="fixed right-0 bottom-0 left-0 z-40">
          <div className="border-t border-[#f1f1f1] bg-white">
            {showApprovalActions ? (
              <TripApprovalActions
                layout="inline"
                onBack={() => router.back()}
                onApprove={handleApproveTripAction}
                onReject={canDecline ? handleRejectTripAction : undefined}
                onEdit={canReturnBack ? handleEditTripAction : undefined}
                showApprove={canApprove}
                showReject={canDecline}
                showEdit={canReturnBack}
                isApproving={approveTripMutation.isPending}
                isRejecting={declineTripMutation.isPending}
                isEditing={returnTripMutation.isPending}
                disableApprove={shouldDisableApproveAction}
                disableReject={declineTripMutation.isPending}
                disableEdit={returnTripMutation.isPending}
                className="lg:px-12 lg:py-4"
              />
            ) : (
              <>
                <div className="hidden items-center justify-between px-12 py-4 lg:flex">
                  {/* Left side - Back button */}
                  <Button
                    variant="ghost"
                    onClick={() => router.back()}
                    className="flex h-auto cursor-pointer items-center justify-center gap-1.5 rounded-[0.75rem] px-4 py-2 hover:bg-[#f9f9f9]"
                  >
                    <img src="/assets/icons/chevron-left.svg" alt="" className="h-5 w-5" />
                    <span className="text-base font-medium text-black">{tNav('back')}</span>
                  </Button>

                  <div className="flex items-center gap-3">
                    {canSendToAccountant && (
                      <Button
                        variant="brand"
                        onClick={handleSendToAccountantClick}
                        className="flex h-12 items-center gap-2 rounded-full px-6 py-3"
                        disabled={sendToAccountantMutation.isPending}
                      >
                        {sendToAccountantMutation.isPending ? (
                          <Spinner variant="circle" className="text-white" />
                        ) : (
                          <>
                            <img src="/assets/icons/file-check.svg" alt="" className="h-5 w-5" />
                            <span className="text-base font-medium text-white">{t('send')}</span>
                          </>
                        )}
                      </Button>
                    )}

                    {showSendButton && (
                      <Button
                        variant="brand"
                        onClick={handleSendClick}
                        className="flex h-12 items-center gap-2 rounded-full px-6 py-3"
                        disabled={sendTripMutation.isPending}
                      >
                        {sendTripMutation.isPending ? (
                          <Spinner variant="circle" className="text-white" />
                        ) : (
                          <>
                            <img src="/assets/icons/file-check.svg" alt="" className="h-5 w-5" />
                            <span className="text-base font-medium text-white">{t('send')}</span>
                          </>
                        )}
                      </Button>
                    )}

                    {showSavePlaceholder && (
                      <Button
                        variant="ghost"
                        className="h-12 cursor-pointer rounded-full bg-[#f9f9f9] px-6 py-3 hover:bg-[#ebebeb]"
                        disabled
                      >
                        <span className="text-base font-medium text-[#bbbbbb]">
                          {t('saveAndContinue')}
                        </span>
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex w-full min-w-0 items-center justify-between gap-2 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] lg:hidden">
                  <Button
                    variant="ghost"
                    onClick={() => router.back()}
                    className="flex h-10 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-medium text-black hover:bg-[#f1f1f1]"
                  >
                    <img src="/assets/icons/chevron-left.svg" alt="" className="h-5 w-5" />
                    {tNav('back')}
                  </Button>

                  <div className="flex min-w-0 items-center justify-end gap-2">
                    {overflowActions.length > 0 && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e4e4e4] bg-white text-[#292929] transition-colors hover:bg-[#f1f1f1]"
                            aria-label={mobileOverflowAriaLabel}
                          >
                            <MoreHorizontalIcon className="h-5 w-5" aria-hidden />
                            {mobileOverflowAriaLabel ? (
                              <span className="sr-only">{mobileOverflowAriaLabel}</span>
                            ) : null}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-[14rem] max-w-[80vw] rounded-2xl border border-[#ededed] bg-white p-1 shadow-[0px_12px_32px_-12px_rgba(31,31,31,0.12)]"
                        >
                          {overflowActions.map((action) => (
                            <DropdownMenuItem
                              key={action.id}
                              onSelect={(event) => {
                                event.preventDefault();
                                if (action.disabled) {
                                  return;
                                }
                                action.onSelect?.();
                              }}
                              disabled={action.disabled}
                              className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm leading-5 ${
                                action.disabled ? 'text-[#bbbbbb]' : 'text-[#292929]'
                              } focus:bg-[#f9f9f9] data-[highlighted]:bg-[#f9f9f9]`}
                            >
                              <span>{action.label}</span>
                              {action.loading ? (
                                <Spinner
                                  variant="circle"
                                  className="text-[#bbbbbb]"
                                  size={16}
                                  strokeWidth={2}
                                />
                              ) : null}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}

                    {primaryAction && (
                      <Button
                        variant={primaryAction.buttonVariant === 'brand' ? 'brand' : 'ghost'}
                        onClick={primaryAction.onPress}
                        disabled={primaryAction.disabled}
                        className={
                          primaryAction.buttonVariant === 'brand'
                            ? 'flex h-11 min-w-0 items-center gap-2 rounded-full px-5 text-sm font-medium whitespace-nowrap text-white transition-transform hover:scale-[0.99] disabled:opacity-50'
                            : 'flex h-11 min-w-0 items-center gap-2 rounded-full bg-[#f9f9f9] px-5 text-sm font-medium whitespace-nowrap text-[#bbbbbb] hover:bg-[#ebebeb] disabled:cursor-not-allowed disabled:opacity-70'
                        }
                      >
                        {primaryAction.loading ? (
                          <Spinner
                            variant="circle"
                            className={
                              primaryAction.buttonVariant === 'brand'
                                ? 'text-white'
                                : 'text-[#bbbbbb]'
                            }
                            size={16}
                            strokeWidth={2}
                          />
                        ) : primaryAction.icon ? (
                          <img src={primaryAction.icon} alt="" className="h-5 w-5" />
                        ) : null}
                        <span className="whitespace-nowrap">{primaryAction.label}</span>
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteTrip}
        title={tTrips('deleteConfirmation.title')}
        message={tTrips('deleteConfirmation.message')}
        confirmText={tTrips('deleteConfirmation.confirm')}
        cancelText={tTrips('deleteConfirmation.cancel')}
        confirmButtonVariant="danger"
        isLoading={deleteTripMutation.isPending}
      />

      <ConfirmationDialog
        isOpen={showSendToAccountantConfirm}
        onClose={() => setShowSendToAccountantConfirm(false)}
        onConfirm={handleSendToAccountant}
        title={tTrips('sendToAccountantConfirmation.title')}
        message={tTrips('sendToAccountantConfirmation.message')}
        confirmText={tTrips('sendToAccountantConfirmation.confirm')}
        cancelText={tTrips('sendToAccountantConfirmation.cancel')}
        confirmButtonVariant="brand"
        isLoading={sendToAccountantMutation.isPending}
      />

      {/* Send Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showSendConfirm}
        onClose={() => setShowSendConfirm(false)}
        onConfirm={handleSendTrip}
        title={tTrips('sendConfirmation.title')}
        message={tTrips('sendConfirmation.message')}
        confirmText={tTrips('sendConfirmation.confirm')}
        cancelText={tTrips('sendConfirmation.cancel')}
        confirmButtonVariant="brand"
        isLoading={sendTripMutation.isPending}
      />

      <ConfirmationDialog
        isOpen={showApproveConfirm}
        onClose={() => setShowApproveConfirm(false)}
        onConfirm={() => {
          approveTripMutation.mutate(tripId, {
            onSuccess: () => {
              setShowApproveConfirm(false);
              showToast({ message: tToasts('tripApproved'), variant: 'success' });
            },
            onError: () => setShowApproveConfirm(false),
          });
        }}
        title={tTrips('approveConfirmation.title')}
        message={tTrips('approveConfirmation.message')}
        confirmText={tTrips('approveConfirmation.confirm')}
        cancelText={tTrips('approveConfirmation.cancel')}
        confirmButtonVariant="brand"
        isLoading={approveTripMutation.isPending}
      />

      <InputDialog
        isOpen={showRejectDialog}
        onClose={() => setShowRejectDialog(false)}
        onConfirm={(comment) => {
          declineTripMutation.mutate(
            { tripId, comment },
            {
              onSuccess: () => setShowRejectDialog(false),
              onError: () => setShowRejectDialog(false),
            }
          );
        }}
        title={tTrips('rejectConfirmation.title')}
        description={tTrips('rejectConfirmation.message')}
        confirmText={tTrips('rejectConfirmation.confirm')}
        cancelText={tTrips('rejectConfirmation.cancel')}
        placeholder={tTrips('rejectConfirmation.commentPlaceholder') ?? ''}
        isLoading={declineTripMutation.isPending}
      />

      <InputDialog
        isOpen={showReturnDialog}
        onClose={() => setShowReturnDialog(false)}
        onConfirm={(comment) => {
          returnTripMutation.mutate(
            { tripId, comment },
            {
              onSuccess: () => setShowReturnDialog(false),
              onError: () => setShowReturnDialog(false),
            }
          );
        }}
        title={tTrips('returnConfirmation.title')}
        description={tTrips('returnConfirmation.message')}
        confirmText={tTrips('returnConfirmation.confirm')}
        cancelText={tTrips('returnConfirmation.cancel')}
        placeholder={tTrips('returnConfirmation.commentPlaceholder') ?? ''}
        isLoading={returnTripMutation.isPending}
      />

      <MissingExpensesDialog
        isOpen={showMissingExpenses}
        onClose={closeMissingExpensesDialog}
        entries={missingExpenses}
      />

      <InvalidCityDatesDialog
        isOpen={showInvalidCityDates}
        onClose={() => setShowInvalidCityDates(false)}
        entries={invalidCityDatesEntries}
      />

      <InvalidHotelDatesDialog
        isOpen={showInvalidHotelDates}
        onClose={() => setShowInvalidHotelDates(false)}
        entries={invalidHotelDatesEntries}
      />

      <IncompleteDateCoverageDialog
        isOpen={showIncompleteCoverage}
        onClose={() => setShowIncompleteCoverage(false)}
        gaps={uncoveredDateGaps}
      />
    </div>
  );
}
